/**
 * Selection Toolbar Component
 * Displays when transactions are selected and provides bulk actions
 */

import { X, FileSpreadsheet, Mail, Receipt, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { formatCurrency } from "@/lib/utils";
import { Transaction } from "@/types/transaction";
import { TransactionTotals } from "@/lib/export-utils";

interface SelectionToolbarProps {
  selectedCount: number;
  totals: TransactionTotals;
  selectedTransactions: Transaction[];
  onClearSelection: () => void;
  onExportSelected: () => void;
  onSendReminders: () => void;
  onAllocateReceipt: () => void;
  onSendCopyInvoice?: () => void;
}

export function SelectionToolbar({
  selectedCount,
  totals,
  selectedTransactions,
  onClearSelection,
  onExportSelected,
  onSendReminders,
  onAllocateReceipt,
  onSendCopyInvoice,
}: SelectionToolbarProps) {
  // Check if selected transactions contain receipts
  const hasReceipts = selectedTransactions.some((t) => t.transactionType === "receipt");

  // Check if selected transactions contain overdue invoices
  const hasOverdueInvoices = selectedTransactions.some(
    (t) => t.transactionType === "invoice" && t.status === "overdue"
  );

  // Check if "Send Copy Invoice" should be shown
  // Only show when all selected are invoices from a single customer
  const shouldShowSendCopyInvoice = (() => {
    if (!onSendCopyInvoice) return false;

    const allInvoices = selectedTransactions.length > 0 &&
      selectedTransactions.every((t) => t.transactionType === "invoice");

    if (!allInvoices) return false;

    const uniqueCustomerIds = new Set(selectedTransactions.map((t) => t.customerId));
    return uniqueCustomerIds.size === 1;
  })();

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
        <div className="flex items-center gap-4 px-6 py-4">
          {/* Selection Info */}
          <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                  {selectedCount}
                </Badge>
                <span className="font-medium">
                  {selectedCount === 1 ? "transaction" : "transactions"} selected
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                <span>
                  Total: <span className="font-semibold">{formatCurrency(totals.totalAmount, totals.currency)}</span>
                </span>
                {totals.totalOutstanding > 0 && (
                  <>
                    <span>•</span>
                    <span>
                      Outstanding: <span className="font-semibold">{formatCurrency(totals.totalOutstanding, totals.currency)}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={onExportSelected}
              className="gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Export Selected
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" className="gap-2">
                  Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {hasOverdueInvoices && (
                  <>
                    <DropdownMenuItem onClick={onSendReminders} className="gap-2">
                      <Mail className="h-4 w-4" />
                      Send Payment Reminders
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {shouldShowSendCopyInvoice && (
                  <>
                    <DropdownMenuItem onClick={onSendCopyInvoice} className="gap-2">
                      <Mail className="h-4 w-4" />
                      Send Copy Invoice
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {hasReceipts && (
                  <>
                    <DropdownMenuItem onClick={onAllocateReceipt} className="gap-2">
                      <Receipt className="h-4 w-4" />
                      Allocate Receipt
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem disabled className="text-muted-foreground">
                  More actions coming soon...
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              className="gap-2 hover:bg-primary-foreground/20"
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
